import { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react'
import { useUser } from './UserContext'
import { createAdminApi, AdminConfig } from '../services/adminApi'

interface AdminContextType {
  apiKey: string
  config: AdminConfig | null
  isAuthenticated: boolean
  loadConfig: () => Promise<void>
  loadStatus: string
  loadError: string
}

const AdminContext = createContext<AdminContextType>(null!)

export function AdminProvider({ children }: { children: ReactNode }) {
  const { userApiKey, isAdmin } = useUser()
  const [config, setConfig] = useState<AdminConfig | null>(null)
  const [loadStatus, setLoadStatus] = useState('')
  const [loadError, setLoadError] = useState('')
  const autoLoaded = useRef(false)

  const isAuthenticated = isAdmin

  const api = createAdminApi(() => userApiKey)

  async function loadConfig() {
    try {
      setLoadError('')
      setLoadStatus('Loading...')
      const cfg = await api.getConfig()
      setConfig(cfg)
      setLoadStatus('Loaded')
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e)
      setConfig(null)
      setLoadStatus('')
      setLoadError(msg)
    }
  }

  useEffect(() => {
    if (isAdmin && !autoLoaded.current) {
      autoLoaded.current = true
      loadConfig()
    }
    if (!isAdmin) {
      setConfig(null)
      autoLoaded.current = false
    }
  }, [isAdmin]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AdminContext.Provider value={{ apiKey: userApiKey, config, isAuthenticated, loadConfig, loadStatus, loadError }}>
      {children}
    </AdminContext.Provider>
  )
}

export const useAdmin = () => useContext(AdminContext)
